import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Send, Eye, FileText, Plus, Trash2, Loader2, Mail,
  UserPlus2, Users, ChevronDown, AlertTriangle, Search,
  Bold, Italic, Underline, Link2, List, ListOrdered,
  Heading1, Heading2, Eraser, Tag,
} from 'lucide-react';
import { TestTube2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token')}` } });

const codeToFlag = (code) => {
  if (!code || code.length !== 2) return '🏳️';
  const up = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + (up.charCodeAt(0) - 65), 0x1F1E6 + (up.charCodeAt(1) - 65));
};

/**
 * DelegateEmailCompose — full-featured compose modal.
 * props:
 *   open          : boolean
 *   onClose       : fn
 *   recipients    : initial list [{email,name,country_code}]
 *   tr            : boolean (UI language)
 *   currentUserEmail: string (for "send test to me")
 */
const ChipInput = ({ values, onChange, placeholder, suggestions = [], onQuery, 'data-testid': dataTestId }) => {
  const [input, setInput] = useState('');
  const [showSug, setShowSug] = useState(false);
  const add = (v) => {
    const val = (v || '').trim().toLowerCase();
    if (!val || !val.includes('@')) return;
    if (values.includes(val)) return;
    onChange([...values, val]);
    setInput('');
  };
  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 items-center min-h-[40px] bg-white border border-gray-200 rounded-xl px-2 py-1.5 focus-within:border-emerald-400">
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-md text-xs px-2 py-0.5">
            <span>{v}</span>
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="text-gray-400 hover:text-red-500">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          data-testid={dataTestId}
          value={input}
          onChange={e => { setInput(e.target.value); onQuery && onQuery(e.target.value); setShowSug(true); }}
          onBlur={() => setTimeout(() => setShowSug(false), 180)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); add(input); }
            if (e.key === 'Backspace' && !input && values.length) onChange(values.slice(0, -1));
          }}
          placeholder={placeholder}
          className="flex-1 min-w-[140px] text-sm outline-none bg-transparent"
        />
      </div>
      {showSug && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.email}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(s.email); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <Mail className="h-3.5 w-3.5 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate">{s.full_name || s.email}</p>
                <p className="text-[11px] text-gray-400 truncate">{s.email} · {s.role}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * RichTextEditor — lightweight WYSIWYG built on contentEditable +
 * document.execCommand. Emits sanitized HTML via onChange(html).
 * Toolbar: bold / italic / underline / H1 / H2 / ul / ol / link / clear
 * Plus a "variable" dropdown to insert {{name}} / {{country}} / {{platform_url}}.
 */
const RichTextEditor = ({ value, onChange, tr, 'data-testid': dataTestId }) => {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const suppress = useRef(false);

  // sync external value → DOM (only when value is set from outside, e.g. load)
  useEffect(() => {
    if (!ref.current) return;
    if (suppress.current) { suppress.current = false; return; }
    if (ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const emit = () => {
    if (!ref.current) return;
    suppress.current = true;
    onChange(ref.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt(tr ? 'Bağlantı URL' : 'Link URL', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const insertVariable = (varName) => {
    ref.current?.focus();
    // Wrap the variable in a chip span so it is visually distinct but still a plain
    // token that the backend substitutes on send.
    document.execCommand(
      'insertHTML',
      false,
      `<span style="background:#ecfdf5;border:1px solid #a7f3d0;color:#047857;border-radius:4px;padding:1px 6px;font-family:monospace;font-size:0.85em;">{{${varName}}}</span>&nbsp;`
    );
    emit();
  };

  const clearFormat = () => {
    exec('removeFormat');
  };

  const Btn = ({ onMouseDown, title, children, testId }) => (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
      className="p-1.5 rounded-md text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 transition"
    >
      {children}
    </button>
  );

  return (
    <div className={`border rounded-xl overflow-hidden transition ${focused ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-center gap-0.5 bg-gray-50 border-b border-gray-200 px-1.5 py-1">
        <Btn onMouseDown={() => exec('bold')}          title={tr ? 'Kalın' : 'Bold'}       testId="rte-bold"><Bold      className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={() => exec('italic')}        title={tr ? 'İtalik' : 'Italic'}    testId="rte-italic"><Italic    className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={() => exec('underline')}     title={tr ? 'Altı çizili' : 'Underline'}testId="rte-underline"><Underline className="h-3.5 w-3.5" /></Btn>
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <Btn onMouseDown={() => exec('formatBlock', '<H1>')} title="H1" testId="rte-h1"><Heading1 className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={() => exec('formatBlock', '<H2>')} title="H2" testId="rte-h2"><Heading2 className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={() => exec('formatBlock', '<P>')}  title={tr ? 'Paragraf' : 'Paragraph'} testId="rte-p"><span className="text-[11px] font-bold">P</span></Btn>
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <Btn onMouseDown={() => exec('insertUnorderedList')} title={tr ? 'Madde listesi' : 'Bullet list'} testId="rte-ul"><List        className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={() => exec('insertOrderedList')}   title={tr ? 'Numaralı liste' : 'Numbered list'} testId="rte-ol"><ListOrdered className="h-3.5 w-3.5" /></Btn>
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <Btn onMouseDown={insertLink}  title={tr ? 'Bağlantı ekle' : 'Insert link'} testId="rte-link"><Link2 className="h-3.5 w-3.5" /></Btn>
        <Btn onMouseDown={clearFormat} title={tr ? 'Biçimi temizle' : 'Clear formatting'} testId="rte-clear"><Eraser className="h-3.5 w-3.5" /></Btn>
        <span className="w-px h-4 bg-gray-300 mx-1" />
        <div className="flex items-center gap-0.5 ml-auto">
          <span className="text-[10px] text-gray-500 mr-1 flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {tr ? 'Değişken' : 'Variable'}
          </span>
          <button type="button" data-testid="rte-var-name"      onMouseDown={(e) => { e.preventDefault(); insertVariable('name'); }}
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">{'{{name}}'}</button>
          <button type="button" data-testid="rte-var-country"   onMouseDown={(e) => { e.preventDefault(); insertVariable('country'); }}
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">{'{{country}}'}</button>
          <button type="button" data-testid="rte-var-url"       onMouseDown={(e) => { e.preventDefault(); insertVariable('platform_url'); }}
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">{'{{platform_url}}'}</button>
        </div>
      </div>
      <div
        ref={ref}
        data-testid={dataTestId}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="min-h-[220px] max-h-[400px] overflow-y-auto px-4 py-3 text-sm leading-relaxed focus:outline-none bg-white"
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
};

const NewTemplateSheet = ({ onClose, onCreated, tr }) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [language, setLanguage] = useState('en');
  const [html, setHtml] = useState(
    '<p>Dear {{name}},</p><p>Welcome to COP 31 Antalya on behalf of {{country}}.</p><p><a href="{{platform_url}}/login">Sign in</a></p>'
  );
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim() || !subject.trim() || !html.trim()) {
      toast.error(tr ? 'Tüm alanları doldurun.' : 'Fill all fields.'); return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ultra-admin/unfccc/email-templates`, { name, subject, language, html_body: html }, getAuth());
      toast.success(tr ? 'Şablon kaydedildi.' : 'Template saved.');
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };
  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col">
      <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-bold text-gray-800">{tr ? 'Yeni Şablon' : 'New Template'}</h4>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-gray-500">
            {tr ? 'Şablon Adı' : 'Template Name'}
            <input value={name} onChange={e => setName(e.target.value)} data-testid="tpl-new-name" className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-emerald-400" />
          </label>
          <label className="text-xs font-semibold text-gray-500">
            {tr ? 'Dil' : 'Language'}
            <select value={language} onChange={e => setLanguage(e.target.value)} className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white">
              <option value="en">English</option>
              <option value="tr">Türkçe</option>
            </select>
          </label>
        </div>
        <label className="text-xs font-semibold text-gray-500 block">
          {tr ? 'Konu' : 'Subject'}
          <input value={subject} onChange={e => setSubject(e.target.value)} data-testid="tpl-new-subject" className="mt-1 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-emerald-400" />
        </label>
        <div className="text-xs font-semibold text-gray-500 block">
          <div className="flex items-center justify-between mb-1">
            <span>{tr ? 'E-posta Gövdesi' : 'Email Body'}</span>
            <span className="text-[10px] font-normal text-gray-400">
              {tr ? 'Biçimlendirme için araç çubuğunu kullanın' : 'Use the toolbar to format'}
            </span>
          </div>
          <RichTextEditor
            value={html}
            onChange={setHtml}
            tr={tr}
            data-testid="tpl-new-html"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            {tr ? 'Değişken eklemek için sağdaki yeşil rozetleri kullanın:' : 'Use the green badges (top-right) to insert variables:'}{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{country}}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{platform_url}}'}</code>
          </p>
        </div>
      </div>
      <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-gray-50">
        <button onClick={onClose} className="text-sm px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-white">
          {tr ? 'Vazgeç' : 'Cancel'}
        </button>
        <button onClick={save} disabled={saving} data-testid="tpl-new-save" className="text-sm px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {tr ? 'Kaydet' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const DelegateEmailCompose = ({ open, onClose, recipients: initial, tr, currentUserEmail }) => {
  const [recipients, setRecipients] = useState([]);
  const [rcpSearch, setRcpSearch] = useState('');
  const [cc, setCc]   = useState([]);
  const [bcc, setBcc] = useState([]);
  const [ccSugg, setCcSugg] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showNewTpl, setShowNewTpl] = useState(false);
  const inited = useRef(false);

  useEffect(() => {
    if (open && !inited.current) {
      setRecipients((initial || []).map(r => ({ email: r.email, name: r.name || '', country_code: r.country_code || '' })));
      setRcpSearch('');
      inited.current = true;
    }
    if (!open) { inited.current = false; setPreview(null); setRcpSearch(''); }
  }, [open, initial]);

  // load templates on open
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/ultra-admin/unfccc/email-templates`, getAuth())
      .then(r => {
        setTemplates(r.data.items || []);
        if (!templateId && (r.data.items || []).length) setTemplateId(r.data.items[0].id);
      })
      .catch(() => toast.error(tr ? 'Şablonlar yüklenemedi.' : 'Failed to load templates.'));
    // eslint-disable-next-line
  }, [open]);

  // preview when template or first recipient changes
  useEffect(() => {
    if (!open || !templateId) return;
    const first = recipients[0];
    const cc_code = first?.country_code || 'FR';
    const name    = first?.name || 'Delegate';
    setPreviewLoading(true);
    axios.get(`${API}/ultra-admin/unfccc/email-template-preview/${templateId}?country_code=${cc_code}&name=${encodeURIComponent(name)}`, getAuth())
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [open, templateId, recipients]);

  const queryCcUsers = async (q) => {
    if (!q || q.length < 2) { setCcSugg([]); return; }
    try {
      const r = await axios.get(`${API}/ultra-admin/unfccc/cc-candidates?q=${encodeURIComponent(q)}`, getAuth());
      setCcSugg(r.data.items || []);
    } catch { setCcSugg([]); }
  };

  const tpl = useMemo(() => templates.find(t => t.id === templateId), [templates, templateId]);

  const filteredRecipients = useMemo(() => {
    const q = rcpSearch.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(r =>
      (r.email || '').toLowerCase().includes(q) ||
      (r.name  || '').toLowerCase().includes(q) ||
      (r.country_code || '').toLowerCase().includes(q)
    );
  }, [recipients, rcpSearch]);

  const doSend = async ({ testMode = false } = {}) => {
    if (!templateId) { toast.error(tr ? 'Şablon seçin.' : 'Pick a template.'); return; }
    if (!testMode && recipients.length === 0) {
      toast.error(tr ? 'En az bir alıcı ekleyin.' : 'Pick at least one recipient.'); return;
    }
    const setter = testMode ? setTesting : setSending;
    setter(true);
    try {
      const r = await axios.post(`${API}/ultra-admin/unfccc/send-custom`, {
        template_id: templateId,
        recipients:  recipients.map(r => r.email),
        cc, bcc,
        test_mode:   testMode,
      }, getAuth());
      const s = r.data.summary;
      const msg = tr
        ? `${s.sent_count} gönderildi (${s.tr_count} TR · ${s.en_count} EN)${s.failed_count ? ` · ${s.failed_count} hata` : ''}`
        : `${s.sent_count} sent (${s.tr_count} TR · ${s.en_count} EN)${s.failed_count ? ` · ${s.failed_count} failed` : ''}`;
      if (s.sent_count) {
        toast.success(msg);
        if (!testMode) onClose();
      } else toast.error(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (tr ? 'Mail gönderilemedi.' : 'Send failed.'));
    } finally {
      setter(false);
    }
  };

  const deleteTpl = async (t) => {
    if (t.is_system) { toast.error(tr ? 'Sistem şablonları silinemez.' : 'System templates cannot be deleted.'); return; }
    if (!window.confirm(tr ? `"${t.name}" şablonu silinsin mi?` : `Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/unfccc/email-templates/${t.id}`, getAuth());
      setTemplates(prev => prev.filter(x => x.id !== t.id));
      if (templateId === t.id) setTemplateId(templates[0]?.id || '');
      toast.success(tr ? 'Şablon silindi.' : 'Template deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delegate-compose-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col relative" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-sky-500 to-emerald-600 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{tr ? 'Delegelere E-posta' : 'Email to Delegates'}</h2>
              <p className="text-xs text-white/80">{tr ? 'Şablon seç · alıcıları düzenle · önizle · gönder' : 'Pick template · edit recipients · preview · send'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body: 2-column */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          {/* Left column — compose form */}
          <div className="overflow-y-auto p-5 space-y-4 border-r border-gray-100">
            {/* Template picker */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">{tr ? 'Şablon' : 'Template'}</label>
                <button onClick={() => setShowNewTpl(true)} data-testid="tpl-new-btn" className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                  <Plus className="h-3 w-3" />
                  {tr ? 'Şablon Ekle' : 'Add Template'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <select
                    value={templateId}
                    onChange={e => setTemplateId(e.target.value)}
                    data-testid="tpl-picker"
                    className="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-xl bg-white appearance-none focus:outline-none focus:border-emerald-400"
                  >
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} · {t.language.toUpperCase()}{t.is_system ? ' · 🔒' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="h-4 w-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {tpl && !tpl.is_system && (
                  <button onClick={() => deleteTpl(tpl)} className="p-2 rounded-xl border border-red-100 text-red-500 hover:bg-red-50" title={tr ? 'Bu şablonu sil' : 'Delete template'}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {tpl?.is_system && (
                <p className="mt-1 text-[11px] text-gray-400">
                  {tr ? 'Sistem şablonu — Türk delegelere Türkçe sürüm otomatik gönderilir.' : 'System template — Turkish delegates receive the TR version automatically.'}
                </p>
              )}
            </div>

            {/* Recipients */}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide flex items-center gap-2 mb-1">
                <Users className="h-3.5 w-3.5" />
                {tr
                  ? `Alıcılar (${filteredRecipients.length}${rcpSearch ? ` / ${recipients.length}` : ''})`
                  : `Recipients (${filteredRecipients.length}${rcpSearch ? ` / ${recipients.length}` : ''})`}
              </label>
              {recipients.length > 0 && (
                <div className="relative mb-1.5">
                  <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={rcpSearch}
                    onChange={(e) => setRcpSearch(e.target.value)}
                    placeholder={tr ? 'Ara: isim, e-posta, ülke kodu…' : 'Search: name, email, country code…'}
                    data-testid="compose-recipient-search"
                    className="w-full text-xs bg-white border border-gray-200 rounded-lg pl-8 pr-8 py-1.5 focus:outline-none focus:border-emerald-400"
                  />
                  {rcpSearch && (
                    <button
                      type="button"
                      onClick={() => setRcpSearch('')}
                      data-testid="compose-recipient-search-clear"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title={tr ? 'Temizle' : 'Clear'}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
              <div className="border border-gray-200 rounded-xl p-2 max-h-56 overflow-y-auto space-y-1.5 bg-gray-50">
                {recipients.length === 0 && (
                  <div className="text-xs text-gray-400 italic text-center py-4">{tr ? 'Hiç alıcı yok.' : 'No recipients.'}</div>
                )}
                {recipients.length > 0 && filteredRecipients.length === 0 && (
                  <div className="text-xs text-gray-400 italic text-center py-4">
                    {tr ? `"${rcpSearch}" için eşleşme yok.` : `No match for "${rcpSearch}".`}
                  </div>
                )}
                {filteredRecipients.map((r) => (
                  <div key={r.email} data-testid={`compose-recipient-${r.email}`} className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-2 py-1.5">
                    <span className="text-lg leading-none">{codeToFlag(r.country_code)}</span>
                    <div className="flex-1 min-w-0">
                      {r.name && <p className="text-sm font-semibold text-gray-800 truncate">{r.name}</p>}
                      <p className="text-[11px] text-gray-500 truncate">{r.email}</p>
                    </div>
                    <button
                      onClick={() => setRecipients(prev => prev.filter((x) => x.email !== r.email))}
                      className="p-1 rounded-md text-gray-400 hover:text-red-500"
                      title={tr ? 'Kaldır' : 'Remove'}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* CC */}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide flex items-center gap-2 mb-1">
                <UserPlus2 className="h-3.5 w-3.5" />
                CC
                <span className="font-normal text-[10px] text-gray-400 normal-case">
                  {tr ? '(Enter/virgül · veya kullanıcı ara)' : '(Enter/comma · or search user)'}
                </span>
              </label>
              <ChipInput
                values={cc}
                onChange={setCc}
                onQuery={queryCcUsers}
                suggestions={ccSugg.filter(s => !cc.includes(s.email) && !recipients.some(r => r.email === s.email))}
                placeholder="user@example.org"
                data-testid="compose-cc-input"
              />
            </div>

            {/* BCC */}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1 block">BCC</label>
              <ChipInput values={bcc} onChange={setBcc} placeholder="hidden@example.org" data-testid="compose-bcc-input" />
            </div>

            {/* Test send */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
              <TestTube2 className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-xs">
                <p className="font-semibold text-amber-900">{tr ? 'Kendine test gönder' : 'Send test to yourself'}</p>
                <p className="text-amber-700 mt-0.5">
                  {tr
                    ? `Sadece ${currentUserEmail} adresine gider — alıcılar/CC/BCC atlanır.`
                    : `Goes only to ${currentUserEmail} — recipients/CC/BCC are skipped.`}
                </p>
              </div>
              <button
                onClick={() => doSend({ testMode: true })}
                disabled={testing || !templateId}
                data-testid="compose-send-test"
                className="text-xs font-semibold text-amber-900 bg-amber-200 hover:bg-amber-300 disabled:opacity-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5 shrink-0"
              >
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube2 className="h-3.5 w-3.5" />}
                {tr ? 'Test Gönder' : 'Send Test'}
              </button>
            </div>
          </div>

          {/* Right column — preview */}
          <div className="overflow-hidden flex flex-col bg-gray-50">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-bold text-gray-700">{tr ? 'Önizleme' : 'Preview'}</h3>
                {tpl && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tpl.language === 'tr' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {tpl.language.toUpperCase()}
                  </span>
                )}
              </div>
              {recipients[0] && (
                <span className="text-[11px] text-gray-400 truncate">
                  {tr ? 'Örnek:' : 'Sample:'} {codeToFlag(recipients[0].country_code)} {recipients[0].name || recipients[0].email}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-5" data-testid="compose-preview-area">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
                </div>
              ) : preview ? (
                <>
                  <div className="bg-white rounded-xl border border-gray-100 p-3 mb-3">
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-400">Subject</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{preview.subject}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <iframe
                      title="email-preview"
                      srcDoc={`<!doctype html><html><body style="margin:0">${preview.html}</body></html>`}
                      className="w-full"
                      style={{ height: 'calc(92vh - 320px)', border: 'none' }}
                    />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <AlertTriangle className="h-8 w-8 mb-2" />
                  <p className="text-sm">{tr ? 'Önizleme yok.' : 'No preview.'}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500">
            {tr ? 'Türk delegeler için Türkçe şablon otomatik seçilir.' : 'Turkish delegates auto-receive the TR template.'}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-white">
              {tr ? 'İptal' : 'Cancel'}
            </button>
            <button
              onClick={() => doSend({ testMode: false })}
              disabled={sending || recipients.length === 0 || !templateId}
              data-testid="compose-send"
              className="text-sm px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center gap-2"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {tr ? `Gönder (${recipients.length})` : `Send (${recipients.length})`}
            </button>
          </div>
        </div>

        {showNewTpl && (
          <NewTemplateSheet
            tr={tr}
            onClose={() => setShowNewTpl(false)}
            onCreated={(t) => {
              setTemplates(prev => [...prev, t]);
              setTemplateId(t.id);
              setShowNewTpl(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DelegateEmailCompose;
